import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import { useParams, Link } from "react-router-dom";
import { adminService } from "../../services/adminService";
import toast from "react-hot-toast";

const BulkUpload = () => {
  const { courseId } = useParams();

  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState("");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [parsingError, setParsingError] = useState("");
  const [uploading, setUploading] = useState(false);

  /* =====================================================
     LOAD COHORTS
  ====================================================== */
  useEffect(() => {
    const loadCohorts = async () => {
      try {
        const all = await adminService.getCohorts();
        const filtered = all.filter(
          (c) => String(c.course_id) === String(courseId)
        );
        setCohorts(filtered);
      } catch (err) {
        console.error("Failed to load cohorts", err);
        toast.error("Failed to load cohorts.");
      }
    };
    loadCohorts();
  }, [courseId]);

  /* =====================================================
     CSV PARSER
  ====================================================== */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setParsingError("");
    setRows([]);
    setSummary(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase(),
      complete: (result) => {
        if (!result.data || result.data.length === 0) {
          setParsingError("CSV is empty or has no valid rows.");
          toast.error("CSV has no valid data.");
          return;
        }

        const cleaned = result.data.map((r) => ({
          email: r.email?.trim() || "",
          first_name: r.first_name?.trim() || "",
          last_name: r.last_name?.trim() || "",
        }));

        setRows(cleaned);
        toast.success(`Loaded ${cleaned.length} rows.`);
      },
      error: (err) => {
        console.error("CSV Parse Error:", err);
        setParsingError("Failed to parse CSV.");
        toast.error("CSV parsing failed.");
      },
    });
  };

  /* =====================================================
     SUBMIT UPLOAD
  ====================================================== */
  const handleSubmit = async () => {
    if (rows.length === 0) {
      toast.error("No rows to upload.");
      return;
    }

    setUploading(true);
    setSummary(null);

    try {
      let res;

      if (selectedCohort) {
        res = await adminService.bulkUploadToCohort(
          courseId,
          selectedCohort,
          rows
        );
      } else {
        res = await adminService.bulkUpload(courseId, rows);
      }

      setSummary(res);
      toast.success("Bulk upload completed.");
    } catch (err) {
      console.error("Bulk upload failed", err);
      setSummary({
        errors: ["Upload failed. See console for details."],
      });
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const resetAll = () => {
    setRows([]);
    setSummary(null);
    setParsingError("");
    setSelectedCohort("");
    toast("Reset completed.", { icon: "🔄" });
  };

  /* =====================================================
     RENDER
  ====================================================== */
  return (
    <div className="p-6 space-y-6">

      <Link to={`/admin/courses/${courseId}`} className="text-sm text-muted">
        ← Back to Course
      </Link>

      <h1 className="text-display">Bulk Upload Learners</h1>
      <p className="text-muted">
        Required: <b>email</b>. Optional: <b>first_name</b>, <b>last_name</b>.
      </p>

      {/* ===========================
          COHORT SELECT
      ============================ */}
      {cohorts.length > 0 && (
        <div className="bg-white stakeholder-summary-card rounded-lg p-6">
          <label className="block mb-2 text-title">Assign to Cohort (optional)</label>

          <select
            className="border stakeholder-input w-full p-3 rounded-md"
            value={selectedCohort}
            onChange={(e) => setSelectedCohort(e.target.value)}
          >
            <option value="">— Upload to Course Only —</option>
            {cohorts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.start_date})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ===========================
          FILE UPLOAD
      ============================ */}
      <div className="bg-white stakeholder-summary-card rounded-lg p-6">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="block border p-3 rounded-md w-full"
        />

        {parsingError && (
          <div className="text-red-600 text-sm mt-2">{parsingError}</div>
        )}
      </div>

      {/* ===========================
          PREVIEW TABLE
      ============================ */}
      {rows.length > 0 && (
        <div className="bg-white stakeholder-summary-card rounded-lg p-6">
          <h2 className="text-title mb-4">
            Preview ({rows.length} rows)
          </h2>

          <div className="overflow-x-auto custom-scrollbar-transparent">
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-100">
                <tr>
                  <Th>Email</Th>
                  <Th>First Name</Th>
                  <Th>Last Name</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <Td>{r.email}</Td>
                    <Td>{r.first_name}</Td>
                    <Td>{r.last_name}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-4 mt-6">
            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="px-4 py-2 bg-[#54B0AF] text-white rounded-md shadow hover:opacity-90 transition"
            >
              {uploading ? "Uploading…" : "Submit Upload"}
            </button>

            <button
              onClick={resetAll}
              disabled={uploading}
              className="px-4 py-2 bg-gray-200 rounded-md text-[#323232] hover:bg-gray-300 transition"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* ===========================
          SUMMARY
      ============================ */}
      {summary && (
        <div className="bg-white stakeholder-summary-card rounded-lg p-6">
          <h2 className="text-title mb-4">Upload Summary</h2>

          {summary.summary && (
            <p className="text-body mb-3">{summary.summary}</p>
          )}

          <SummaryBlock title="Created" color="text-green-700" list={summary.created} />
          <SummaryBlock
            title="Already existed"
            color="text-gray-700"
            list={summary.skipped_existing_user}
          />
          <SummaryBlock
            title="Newly enrolled existing users"
            color="text-blue-700"
            list={summary.enrolled_existing_user}
          />
          <SummaryBlock
            title="Errors"
            color="text-red-700"
            list={summary.errors}
            error
          />
        </div>
      )}
    </div>
  );
};

export default BulkUpload;

/* =====================================================
   SMALL SUBCOMPONENTS
===================================================== */

const Th = ({ children }) => (
  <th className="border p-2 text-left text-sm font-semibold">{children}</th>
);

const Td = ({ children }) => (
  <td className="border p-2 text-sm">{children}</td>
);

const SummaryBlock = ({ title, color, list, error }) => {
  if (!list || list.length === 0) return null;

  return (
    <div className="mb-4">
      <h3 className={`font-semibold ${color}`}>{title} ({list.length})</h3>

      <ul
        className={`text-sm ml-4 list-disc ${
          error ? "text-red-600" : "text-[#323232]"
        }`}
      >
        {list.map((e, idx) => (
          <li key={idx}>{e}</li>
        ))}
      </ul>
    </div>
  );
};
